import FileUploader from "@via/components/file-uploader/file-uploader";
import Modal from "@via/components/modal";
import SelectComponent from "@via/components/select/select";
import {
  SpreadsheetData,
  SpreadsheetEditor,
} from "@via/components/spreadsheet-editor/spreadsheet-editor";
import {
  useCreatePremiumDatasetFile,
  useDownloadDatasetTemplate,
  useProjectDatasetFieldsQuery,
  useUploadSpreadsheet,
  useGetFileCollectionId,
} from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import { useVmetaProjectManagement } from "@via/pages/data/hooks/useVmetaProjectManagement";
import { useFieldManagement } from "@via/pages/data/hooks/useFieldManagement";
import AdditionalFieldsModal from "@via/pages/data/premium-dataset/components/additional-fields-modal";
import ManualAddFieldsModal from "@via/pages/data/premium-dataset/components/manual-add-fields-modal";
import { ModalSize } from "@via/utils/constants";
import { SelectOption } from "@via/utils/interfaces";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNotifications } from "@via/hooks/useNotifications";
import { NOTIFICATION } from "@via/context/notifications-context";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import IndeterminateCheckbox from "@via/components/inputs/indeterminate-checkbox";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { SampleTrackerViewType } from "@via/pages/sample-trackers/sample-trackers-filter";
import { classNames } from "@via/utils/helpers";
import {
  processError,
  compareColumnNames,
  FieldFormData,
  formatSpreadsheetCellValue,
  getInitialColumnVisibility,
  applyDefaultHiddenColumns,
} from "@via/pages/data/premium-dataset/utils/helper";
import { formatSharedTrackerOptionLabel } from "@via/pages/data/utils/shared-tracker-option-label";

interface AddPremiumDatasetFileProps {
  isOpen: boolean;
  datasetId: string;
  onClose: () => void;
  fileCanvasId: string | undefined;
}

const AddPremiumDatasetFile = ({
  isOpen,
  datasetId,
  onClose,
  fileCanvasId,
}: AddPremiumDatasetFileProps) => {
  const { user } = useAuth();
  const { setNotification } = useNotifications();
  const [columnLabels, setColumnLabels] = useState<string[]>([]);
  const [canvasColumnNames, setCanvasColumnNames] = useState<string[]>([]);
  const [spreadsheetData, setSpreadsheetData] = useState<SpreadsheetData>([]);
  const [errors, setErrors] = useState([]);
  const [changedRows, setChangedRows] = useState<Set<number>>(new Set());
  const [isExcelSheetUsed, setIsExcelSheetUsed] = useState(false);
  const [hasDataChanges, setHasDataChanges] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  const [selectedTrackerViewType, setSelectedTrackerViewType] =
    useState<SampleTrackerViewType>(SampleTrackerViewType.My);

  const [additionalColumns, setAdditionalColumns] = useState<string[]>([]);
  const [showAdditionalFieldsModal, setShowAdditionalFieldsModal] =
    useState(false);
  const [collectionId, setCollectionId] = useState("");

  const [showManualAddFieldsModal, setShowManualAddFieldsModal] =
    useState(false);

  // Store original uploaded data for proper merging when new fields are added
  const [originalUploadedData, setOriginalUploadedData] = useState<{
    headers: string[];
    rows: any[][];
  } | null>(null);

  // This mapping is used to map the original column names to the new field names
  const [fieldDataMap, setFieldDataMap] = useState<
    Record<string, FieldFormData>
  >({});
  const processedColumnComparisonKeyRef = useRef<string | null>(null);
  const handleProjectCreated = useCallback((_project: SelectOption) => {
    setHasDataChanges(false);
  }, []);

  const mongoUserId = user?.vmeta?.userId;
  const isMyTrackersView = selectedTrackerViewType === SampleTrackerViewType.My;

  const {
    vmetaProjectOptions,
    selectedVmetaProject,
    setSelectedVmetaProject,
    isLoading: isVmetaProjectsLoading,
    setSearch: debouncedHandleInputChange,
    fetchMoreOptions: fetchMoreVmetaProjects,
    createProject,
    resolvedInitialTrackerViewType,
  } = useVmetaProjectManagement({
    userId: mongoUserId,
    viewType: selectedTrackerViewType,
    enabled: isOpen,
    initialProjectId: fileCanvasId,
    resolveInitialProjectOwnership: true,
    onProjectCreated: handleProjectCreated,
  });

  useEffect(() => {
    if (!isOpen || !resolvedInitialTrackerViewType) return;
    setSelectedTrackerViewType(resolvedInitialTrackerViewType);
  }, [isOpen, resolvedInitialTrackerViewType]);
  const selectedCanvasId = (selectedVmetaProject?.value as string) || "";

  const {
    mutateAsync: createPremiumDatasetFile,
    isPending: isCreatePremiumDatasetFileLoading,
  } = useCreatePremiumDatasetFile();

  const {
    data: downloadDatasetTemplateData,
    mutateAsync: downloadDatasetTemplate,
    isPending: isDownloadTemplateLoading,
    isSuccess: isDownloadTemplateSuccess,
  } = useDownloadDatasetTemplate();

  const {
    data: uploadedSpreadsheetData,
    mutateAsync: uploadSpreadsheet,
    isPending: isUploadSpreadsheetLoading,
    isSuccess: isUploadSpreadsheetSuccess,
  } = useUploadSpreadsheet();

  const {
    data: vmetaDatasetFields,
    isSuccess: isGetProjectDatasetFieldsSuccess,
    isError: isGetProjectDatasetFieldsError,
    error: getProjectDatasetFieldsError,
  } = useProjectDatasetFieldsQuery(selectedCanvasId, "file", isOpen);

  const { mutateAsync: getFileCollectionId } = useGetFileCollectionId();
  const { createFields: createFieldsWithRefresh } = useFieldManagement({
    selectedVmetaProject,
    collectionId,
  });

  // Download template
  useEffect(() => {
    if (isDownloadTemplateSuccess && downloadDatasetTemplateData) {
      try {
        const blob = new Blob([downloadDatasetTemplateData], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${selectedVmetaProject?.label || "template"}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (error) {
        console.log("Error transforming data to blob:", error);
      }
    }
  }, [isDownloadTemplateSuccess, downloadDatasetTemplateData]);

  // Upload spreadsheet
  useEffect(() => {
    if (isUploadSpreadsheetSuccess && uploadedSpreadsheetData) {
      const labels = uploadedSpreadsheetData?.data?.[0] || [];
      const rows = uploadedSpreadsheetData?.data?.slice(1) || [];
      const spreadsheetRows: { value: string }[][] = [];

      // Store original uploaded data for later merging
      setOriginalUploadedData({ headers: labels, rows });

      if (rows.length > 0) {
        // Map each row into the required format
        rows.forEach(row => {
          // check if row is full of null, don't add it
          if (row.every(cell => cell === null || cell === undefined)) {
            return;
          }
          const formattedRow = row.map(cell =>
            formatSpreadsheetCellValue(cell)
          );
          const newIndex = spreadsheetRows.length;
          spreadsheetRows.push(formattedRow);
          setChangedRows(prev => new Set(prev).add(newIndex));
          setHasDataChanges(true);
        });
      } else {
        // Create one empty row if no rows exist
        const emptyRow = labels.map(() => ({ value: "" }));
        spreadsheetRows.push(emptyRow);
        setChangedRows(prev => new Set(prev).add(0));
        setHasDataChanges(false);
      }

      setColumnLabels(labels as string[]);
      setSpreadsheetData(spreadsheetRows);

      // Auto-hide columns with no values
      if (spreadsheetRows.length > 0) {
        const initialVisibility = getInitialColumnVisibility(
          labels as string[],
          spreadsheetRows
        );
        setColumnVisibility(
          applyDefaultHiddenColumns(initialVisibility, labels as string[])
        );
      }
    }
  }, [isUploadSpreadsheetSuccess, uploadedSpreadsheetData]);

  useEffect(() => {
    if (isGetProjectDatasetFieldsSuccess && vmetaDatasetFields) {
      const fields = vmetaDatasetFields?.data?.[0] || [];
      const labels = fields.map(field => field);

      // Store canvas column names separately for comparison
      setCanvasColumnNames(labels);

      setColumnLabels(labels);

      // If we have original uploaded data, merge it with the new field structure
      if (originalUploadedData) {
        const { headers: uploadedHeaders, rows: uploadedRows } =
          originalUploadedData;

        // Create mapping from uploaded column names to their indices
        const uploadedColumnMap = new Map(
          uploadedHeaders.map((header, index) => [
            header.toLowerCase().trim(),
            index,
          ])
        );

        // Create a reverse lookup map for better performance
        const fieldNameToOriginalMap = new Map<string, string>();
        Object.entries(fieldDataMap).forEach(([originalColName, fieldData]) => {
          fieldNameToOriginalMap.set(fieldData.name, originalColName);
        });

        // Create merged rows with correct column alignment
        // Filter out rows where all cells are null or undefined
        const mergedRows = uploadedRows
          .filter(uploadedRow => {
            // Check if row has at least one non-null/non-undefined cell
            return !uploadedRow.every(
              cell => cell === null || cell === undefined
            );
          })
          .map(uploadedRow => {
            return labels.map(columnLabel => {
              // Find the original column name for this field
              const originalColumnName =
                fieldNameToOriginalMap.get(columnLabel) || columnLabel;

              const uploadedIndex = uploadedColumnMap.get(
                originalColumnName.toLowerCase().trim()
              );
              return formatSpreadsheetCellValue(
                uploadedIndex !== undefined
                  ? (uploadedRow[uploadedIndex] ?? "")
                  : ""
              );
            });
          });
        setSpreadsheetData(mergedRows);

        // Mark rows as changed since they contain new data
        const changedRowsSet = new Set(mergedRows.map((_, index) => index));
        setChangedRows(changedRowsSet);
        setHasDataChanges(true);

        // Recalculate column visibility based on merged data
        if (mergedRows.length > 0) {
          const updatedVisibility = getInitialColumnVisibility(
            labels,
            mergedRows
          );
          setColumnVisibility(
            applyDefaultHiddenColumns(updatedVisibility, labels)
          );
        }
      } else {
        const initialRows = Array.from({ length: 1 }, () =>
          labels.map(() => ({ value: "" }))
        );
        setSpreadsheetData(initialRows);

        // Show all columns by default when no file is uploaded
        const allVisible = labels.reduce(
          (acc, label) => {
            acc[label] = true;
            return acc;
          },
          {} as Record<string, boolean>
        );
        setColumnVisibility(applyDefaultHiddenColumns(allVisible, labels));
      }
    }
  }, [
    isGetProjectDatasetFieldsSuccess,
    vmetaDatasetFields,
    originalUploadedData,
    fieldDataMap,
  ]);

  useEffect(() => {
    if (!isGetProjectDatasetFieldsError || !getProjectDatasetFieldsError)
      return;

    setNotification({
      id: "project-dataset-fields-fetch-failed",
      message: "Project Dataset Fields Fetch Failed",
      details:
        getProjectDatasetFieldsError.response?.data?.message ??
        "We were unable to upload your file",
      variant: NOTIFICATION.ERROR,
    });
  }, [
    isGetProjectDatasetFieldsError,
    getProjectDatasetFieldsError,
    setNotification,
  ]);

  // Column comparison logic - triggers when both uploaded file and canvas fields are available
  useEffect(() => {
    const handleColumnComparison = async () => {
      if (
        isUploadSpreadsheetSuccess &&
        uploadedSpreadsheetData?.data?.[0] &&
        canvasColumnNames.length > 0 &&
        selectedCanvasId // Ensure a canvas is selected
      ) {
        const uploadedColumns = uploadedSpreadsheetData.data[0] as string[];

        // Filter out null/undefined values and ensure we have valid column names
        const validUploadedColumns = uploadedColumns.filter(
          col =>
            col !== null &&
            col !== undefined &&
            typeof col === "string" &&
            col.trim() !== ""
        );

        // Only proceed with comparison if we have valid columns
        if (validUploadedColumns.length > 0) {
          const comparisonKey = `${selectedCanvasId}::${validUploadedColumns
            .map(col => col.toLowerCase().trim())
            .join("|")}::${canvasColumnNames
            .map(col => col.toLowerCase().trim())
            .join("|")}`;

          if (processedColumnComparisonKeyRef.current === comparisonKey) return;
          processedColumnComparisonKeyRef.current = comparisonKey;

          const comparison = compareColumnNames(
            validUploadedColumns,
            canvasColumnNames
          );

          if (comparison.hasAdditionalColumns) {
            setAdditionalColumns(comparison.additionalColumns);
            // Get collection ID for the file collection
            try {
              const id = await getFileCollectionId(selectedCanvasId);
              setCollectionId(id);
              setShowAdditionalFieldsModal(true);
            } catch (error) {
              processedColumnComparisonKeyRef.current = null;
              console.error("Failed to get collection ID:", error);
              setNotification({
                id: "collection-id-error",
                message: "Failed to Get Collection Information",
                details:
                  "Could not retrieve collection information for field creation.",
                variant: NOTIFICATION.ERROR,
              });
            }
          }
        }
      }
    };

    handleColumnComparison();
  }, [
    isUploadSpreadsheetSuccess,
    uploadedSpreadsheetData,
    selectedVmetaProject?.value,
    canvasColumnNames,
    getFileCollectionId,
    setNotification,
  ]);

  const handleFileUpload = (files: File[]) => {
    const file = files[0];
    uploadSpreadsheet(file);
    setErrors([]);
  };

  const updateSpreadsheetData = (
    data: SpreadsheetData,
    rowIndex?: number,
    rowIndices?: number[]
  ) => {
    setSpreadsheetData(data);
    const changedRowIndices =
      rowIndices && rowIndices.length > 0
        ? rowIndices
        : rowIndex !== undefined
          ? [rowIndex]
          : [];
    if (changedRowIndices.length === 0) {
      return;
    }
    setChangedRows(prev => new Set([...prev, ...changedRowIndices]));
    setHasDataChanges(true);
  };

  const handleVmetaProjectChange = useCallback(
    (selected: SelectOption | readonly SelectOption[] | null) => {
      const next = Array.isArray(selected) ? (selected[0] ?? null) : selected;
      setSelectedVmetaProject(next);
      setHasDataChanges(false);
      setChangedRows(new Set());
      setErrors([]);
    },
    [setSelectedVmetaProject]
  );

  const handleConfirm = async () => {
    const files: Record<string, unknown>[] = [];
    const internalErrors = [];
    setErrors([]);

    try {
      // Only process rows that have been modified
      const changedRowsArray = Array.from(changedRows);

      changedRowsArray.forEach(rowIndex => {
        const row = spreadsheetData[rowIndex];

        // Convert spreadsheetData to files array
        const file: Record<string, unknown> = {};
        row.forEach((cell, colIndex) => {
          const label = columnLabels[colIndex];
          // Only include visible columns
          if (label && columnVisibility[label] !== false) {
            const stringValue = cell.value?.toString() ?? "";
            const value = stringValue === "" ? null : stringValue;
            file[label] =
              value != null && (value.startsWith("{") || value.startsWith("["))
                ? JSON.parse(value)
                : value;
          }
        });
        files.push(file);
      });

      const promises = files.map((file, index) =>
        createPremiumDatasetFile({
          id: datasetId,
          canvasId: (selectedVmetaProject?.value as string) || "",
          file,
        }).catch(err => {
          return { error: err, index };
        })
      );
      // Execute all promises concurrently
      const results = await Promise.allSettled(promises);

      const errorRowIndices = new Set<number>();

      results.forEach((result, i) => {
        if (result.status === "rejected") {
          processError(result.reason, changedRowsArray[i], internalErrors);
          errorRowIndices.add(changedRowsArray[i]);
        } else if (result.status === "fulfilled" && "error" in result.value) {
          processError(result.value.error, changedRowsArray[i], internalErrors);
          errorRowIndices.add(changedRowsArray[i]);
        }
      });

      if (internalErrors.length) {
        setChangedRows(errorRowIndices);
        setErrors(internalErrors);
      } else {
        setChangedRows(new Set());
        onClose();
      }
    } catch (_error) {
      setNotification({
        id: "error-transforming-data",
        message: "Unable to process file",
        details: "Please ensure the data is in the correct format.",
        variant: NOTIFICATION.ERROR,
      });
    }
  };

  const handleCreateFields = async (
    fieldsToCreate: FieldFormData[],
    fieldDataMap: Record<string, FieldFormData>
  ) => {
    try {
      // Store the field data mapping for use in data merging
      setFieldDataMap(fieldDataMap);
      await createFieldsWithRefresh(fieldsToCreate);

      setShowAdditionalFieldsModal(false);
      setAdditionalColumns([]);
    } catch (error) {
      console.error("Failed to create additional fields:", error);
    }
  };

  const handleSkipFieldCreation = () => {
    setShowAdditionalFieldsModal(false);
    setAdditionalColumns([]);
    // Proceed with normal flow without creating additional fields
  };

  const handleDownload = () => {
    downloadDatasetTemplate({
      canvasId: selectedVmetaProject?.value as string,
      collectionName: "file",
    });
  };

  const handleClose = () => {
    // Clean up state when modal is closed
    setOriginalUploadedData(null);
    setAdditionalColumns([]);
    setShowAdditionalFieldsModal(false);
    setShowManualAddFieldsModal(false);
    setSpreadsheetData([]);
    setColumnLabels([]);
    setCanvasColumnNames([]);
    setChangedRows(new Set());
    setHasDataChanges(false);
    setErrors([]);
    setFieldDataMap({});
    setColumnVisibility({});
    processedColumnComparisonKeyRef.current = null;
    onClose();
  };

  // Manual field addition handlers
  const handleManualAddFields = async () => {
    try {
      // Get collection ID for the selected canvas
      const id = await getFileCollectionId(
        selectedVmetaProject?.value as string
      );
      setCollectionId(id);
      setShowManualAddFieldsModal(true);
    } catch (error) {
      console.error("Failed to get collection ID:", error);
      setNotification({
        id: "collection-id-error",
        message: "Failed to Get Collection Information",
        details:
          "Could not retrieve collection information for field creation.",
        variant: NOTIFICATION.ERROR,
      });
    }
  };

  const handleManualFieldsCreated = async (fieldsToCreate: FieldFormData[]) => {
    try {
      await createFieldsWithRefresh(fieldsToCreate);
      setShowManualAddFieldsModal(false);
    } catch (error) {
      console.error("Failed to create manual fields:", error);
    }
  };

  return (
    <Modal
      open={isOpen}
      setOpen={handleClose}
      title="Add Dataset File"
      size={ModalSize.Large}
      width={1000}
      showButtons={false}>
      <div className="flex-2 min-w-80 my-4">
        <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
          Select Sample Tracker
        </label>
        <div className="mb-3">
          <span className="isolate inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() =>
                setSelectedTrackerViewType(SampleTrackerViewType.My)
              }
              className={classNames(
                "relative inline-flex h-9 items-center justify-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 whitespace-nowrap rounded-l-md min-w-[110px]",
                selectedTrackerViewType === SampleTrackerViewType.My
                  ? "bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md"
                  : "bg-white"
              )}>
              My Trackers
            </button>
            <button
              type="button"
              onClick={() =>
                setSelectedTrackerViewType(SampleTrackerViewType.Shared)
              }
              className={classNames(
                "relative inline-flex h-9 items-center justify-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 whitespace-nowrap -ml-px min-w-[150px] rounded-r-md",
                selectedTrackerViewType === SampleTrackerViewType.Shared
                  ? "bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md"
                  : "bg-white"
              )}>
              Shared With Me
            </button>
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <div className="flex-1">
            <SelectComponent
              name="vmetaProjectId"
              options={vmetaProjectOptions}
              value={selectedVmetaProject}
              onChange={handleVmetaProjectChange}
              formatOptionLabel={(option, context) =>
                formatSharedTrackerOptionLabel(
                  option,
                  context,
                  selectedTrackerViewType
                )
              }
              isLoading={isVmetaProjectsLoading}
              isCreatable={isMyTrackersView}
              showCreateHint={isMyTrackersView}
              onCreateOption={isMyTrackersView ? createProject : undefined}
              placeholder={
                isMyTrackersView
                  ? "Choose sample tracker or type to create"
                  : "Choose a sample tracker shared with you"
              }
              enableClear={false}
              onFetchMoreOptions={fetchMoreVmetaProjects}
              onInputChange={inputValue =>
                debouncedHandleInputChange(inputValue)
              }
            />
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              disabled={!selectedVmetaProject}
              onClick={handleManualAddFields}
              className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 disabled:cursor-not-allowed">
              Add Fields
            </button>
          </div>
          <div className="mt-3 flex flex-row items-center">
            <label className="block pr-4 text-sm leading-6 text-gray-900">
              Upload spreadsheet
            </label>
            <IndeterminateCheckbox
              checked={isExcelSheetUsed}
              onChange={() => setIsExcelSheetUsed(!isExcelSheetUsed)}
              onClick={e => {
                e.stopPropagation();
              }}
            />
          </div>
        </div>
        {isExcelSheetUsed && (
          <div className="flex my justify-center">
            <button
              id="download-button"
              type="button"
              disabled={!selectedVmetaProject || isDownloadTemplateLoading}
              onClick={e => {
                e.preventDefault();
                handleDownload();
              }}
              className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Download Template
            </button>
          </div>
        )}
      </div>
      {isExcelSheetUsed && (
        <FileUploader
          onUpload={handleFileUpload}
          maxFiles={1}
          isLoading={false}
          progressPercentageList={[]}
          acceptedTypes={[".csv", ".tsv", ".xls", ".xlsx"]}
          containerClassName="w-full"
        />
      )}
      <div className="my-4 max-h-[250px] overflow-auto">
        <MessageBanner messages={errors} type={BannerType.Error} />
      </div>
      {spreadsheetData.length > 0 && (
        <div className="mb-5">
          <SpreadsheetEditor
            columnLabels={columnLabels}
            data={spreadsheetData}
            onChange={updateSpreadsheetData}
            isLoading={
              isCreatePremiumDatasetFileLoading || isUploadSpreadsheetLoading
            }
            disableContextMenu
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
          />
        </div>
      )}
      <div className="flex justify-end mt-4">
        <button
          id="cancel-button"
          type="button"
          className="ml-4 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="create-button"
          type="button"
          disabled={
            !selectedVmetaProject ||
            isCreatePremiumDatasetFileLoading ||
            isUploadSpreadsheetLoading ||
            !hasDataChanges
          }
          onClick={e => {
            e.preventDefault();
            handleConfirm();
          }}
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          Create
        </button>
      </div>
      {showAdditionalFieldsModal && (
        <AdditionalFieldsModal
          isOpen={showAdditionalFieldsModal}
          onClose={() => setShowAdditionalFieldsModal(false)}
          additionalColumns={additionalColumns}
          onFieldsCreated={handleCreateFields}
          onSkip={handleSkipFieldCreation}
        />
      )}

      {showManualAddFieldsModal && (
        <ManualAddFieldsModal
          isOpen={showManualAddFieldsModal}
          onClose={() => setShowManualAddFieldsModal(false)}
          onFieldsCreated={handleManualFieldsCreated}
        />
      )}
    </Modal>
  );
};

export default AddPremiumDatasetFile;
